import type { ReactNode } from 'react';

import MuiAvatar from '@mui/material/Avatar';
import Tooltip from '@mui/material/Tooltip';
import type { SxProps, Theme } from '@mui/material';

import { EMPTY_ARRAY, EMPTY_OBJECT, EMPTY_STRING } from '@e-socna/empty';
import { colorGenerator } from '@e-socna/colors';
import { checkIfIsArray } from '@e-socna/utils';

interface Props {
  readonly bgColor?: string;
  readonly icon?: ReactNode;
  readonly imageUri?: string | null;
  readonly label?: string;
  readonly fullName?: string;
  readonly shortName?: string;
  readonly showTooltip?: boolean;
  readonly sx?: SxProps<Theme>;
}

export const Avatar = ({
  bgColor,
  icon,
  imageUri,
  label,
  fullName,
  shortName,
  showTooltip = true,
  sx,
}: Readonly<Props>) => {
  let letters: string | undefined;

  if (shortName) {
    letters = shortName;
  } else if (fullName) {
    const splittedName = fullName.split(' ');
    const firstLetters = splittedName.map((n) => n[0] ?? EMPTY_STRING);
    letters = firstLetters.join(EMPTY_STRING).toUpperCase();
  }

  const avatar = (
    <MuiAvatar
      alt={label}
      aria-label={label}
      // eslint-disable-next-line react/jsx-props-no-spreading
      {...(imageUri ? { src: imageUri } : EMPTY_OBJECT)}
      sx={[
        {
          bgcolor: bgColor ?? colorGenerator(letters ?? label),
        },
        ...(!sx ? EMPTY_ARRAY : checkIfIsArray(sx) ? sx : [sx]),
      ]}
    >
      {letters ?? icon}
    </MuiAvatar>
  );

  return showTooltip ? <Tooltip title={label}>{avatar}</Tooltip> : avatar;
};
